// frontend/src/components/ZDocumentWorkflow.jsx
import { useState, useEffect, useRef } from "react";
import { LoaderCircle } from "lucide-react";
import API_BASE_URL from "../config";
import useDocumentStatusStream from "../hooks/useDocumentStatusStream";
import ComplianceSheet from "./approval/ComplianceSheet";
import CompliancePipeline from "./approval/CompliancePipeline";
import DiscoveryView from "./approval/DiscoveryView";
import ProductSheets from "./approval/ProductSheets";
import "./LVWorkflow.css";
import "./approval/CompliancePipeline.css";

const BUILDING_ADVANCE_DELAY_MS = 500;
const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

const STEPS = [
  { id: 1, label: "Upload — document sent to server" },
  { id: 2, label: "OCR Processing — text & tables extracted" },
  { id: 3, label: "Graph Build — nodes indexed in knowledge graph" },
  { id: 4, label: "Ready for Approval Analysis" },
];

// Templates of the Production Compliance Specification panel, in display order.
const COMPLIANCE_MODES = [
  { id: "discovery", label: "Auto (any document)" },
  { id: "pipeline", label: "Zulassung template" },
  { id: "quick", label: "Quick sheet" },
  { id: "products", label: "Produktdatenblätter" },
];

const COMPLETED_THROUGH = {
  idle: 0,
  uploading: 0,
  processing: 1,
  building: 2,
  completed: 4,
  failed: 0,
};

const ACTIVE_STEP = {
  idle: null,
  uploading: 1,
  processing: 2,
  building: 3,
  completed: null,
  failed: null,
};

function delay(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

export default function ZDocumentWorkflow({ database, onStatusChange, onRunApproved, initialDiscoverDoc = "" }) {
  const [phase, setPhase] = useState("idle");
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [docId, setDocId] = useState(null);
  const [docFilename, setDocFilename] = useState("");
  // Bumped when an upload completes, to refresh the Compliance Sheet's doc list.
  const [docsVersion, setDocsVersion] = useState(0);
  // "pipeline" (structured multi-agent) | "quick" (single-prompt sheet)
  const [complianceMode, setComplianceMode] = useState("discovery");

  // When opened from a tray job, force discovery mode so DiscoveryView preselects it.
  useEffect(() => {
    if (initialDiscoverDoc) setComplianceMode("discovery");
  }, [initialDiscoverDoc]);

  const databaseRef = useRef(database);
  useEffect(() => {
    databaseRef.current = database;
  }, [database]);

  useEffect(() => {
    if (phase === "completed") setDocsVersion((v) => v + 1);
  }, [phase]);

  // Notify parent card of phase changes
  useEffect(() => {
    if (!onStatusChange) return;
    if (phase === "idle" || phase === "failed" || phase === "completed") {
      onStatusChange("idle", "Idle");
    } else {
      const step = ACTIVE_STEP[phase] ?? COMPLETED_THROUGH[phase] + 1;
      onStatusChange("running", `Running · step ${step}/4`);
    }
  }, [phase, onStatusChange]);

  // Document processing status — SSE primary, safety-net poll fallback (see
  // useDocumentStatusStream), active only during "processing".
  const { status: docStatus } = useDocumentStatusStream(docId, {
    enabled: phase === "processing",
  });
  useEffect(() => {
    if (phase !== "processing" || !docStatus) return;
    if (docStatus.status === "completed") {
      setPhase("building");
    } else if (docStatus.status === "failed") {
      setError(docStatus.message || "Processing failed");
      setPhase("failed");
    }
  }, [phase, docStatus]);

  // Building phase — brief visual beat on step 3, then complete
  useEffect(() => {
    if (phase !== "building") return;
    let cancelled = false;
    delay(BUILDING_ADVANCE_DELAY_MS).then(() => {
      if (!cancelled) setPhase("completed");
    });
    return () => {
      cancelled = true;
    };
  }, [phase]);

  // ── Event handlers ──────────────────────────────────

  const applyFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      setError("Only PDF files are allowed");
      return;
    }
    if (f.size > MAX_UPLOAD_BYTES) {
      setError("File too large (max 50 MB)");
      return;
    }
    setFile(f);
    setError(null);
  };

  const handleFileChange = (e) => applyFile(e.target.files?.[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    applyFile(e.dataTransfer.files?.[0]);
  };

  const handleUpload = async () => {
    if (!file) return;
    setPhase("uploading");
    setError(null);
    const formData = new FormData();
    formData.append("files", file);
    const db = databaseRef.current;
    const qs = db ? `?database=${encodeURIComponent(db)}` : "";
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/documents/upload${qs}`, {
        method: "POST",
        body: formData,
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || "Upload failed");
      }
      const [doc] = await res.json();
      setDocId(doc.document_id);
      setDocFilename(doc.filename || file.name);
      setPhase("processing");
    } catch (err) {
      setError(err.message);
      setPhase("failed");
    }
  };

  const reset = () => {
    setPhase("idle");
    setFile(null);
    setError(null);
    setDocId(null);
    setDocFilename("");
  };

  // ── Step state ──────────────────────────────────────

  const getStepState = (stepId) => {
    const completed = COMPLETED_THROUGH[phase] ?? 0;
    if (stepId <= completed) return "completed";
    const active = ACTIVE_STEP[phase];
    if (stepId === active) return "active";
    return "pending";
  };

  // ── Render ──────────────────────────────────────────

  const showUpload = phase === "idle" || phase === "uploading";
  const showPipeline = phase !== "idle" && phase !== "failed";

  return (
    <div className="lvwf-root">
      {/* Upload zone */}
      {showUpload && (
        <div
          className={`lvwf-dropzone ${file ? "has-file" : ""}`}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
        >
          <input
            type="file"
            id="zdwf-input"
            accept=".pdf"
            onChange={handleFileChange}
            disabled={phase === "uploading"}
            className="lvwf-file-input"
          />
          <label htmlFor="zdwf-input" className="lvwf-dropzone-label">
            {phase === "uploading" ? (
              <>
                <LoaderCircle size={16} className="ds-spin" aria-hidden="true" />
                <span>Uploading…</span>
              </>
            ) : file ? (
              <span className="lvwf-filename">📄 {file.name}</span>
            ) : (
              <>
                <span className="lvwf-drop-icon">⬆</span>
                <span>
                  Drop Z-document PDF here or <u>browse</u>
                </span>
                <span className="lvwf-hint">PDF · max 50 MB · Z-17x documents</span>
              </>
            )}
          </label>
          {file && phase === "idle" && (
            <button className="ds-btn ds-btn--primary" onClick={handleUpload}>
              Upload Z-Document →
            </button>
          )}
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="ds-alert ds-alert--danger ds-flush">
          <span>{error}</span>
          <button onClick={reset} className="ds-btn ds-btn--secondary ds-btn--sm ds-push-end">
            Try again
          </button>
        </div>
      )}

      {/* Pipeline steps */}
      {showPipeline && (
        <div className="lvwf-pipeline">
          <div className="lvwf-pipeline-hdr">
            <span className="lvwf-live-dot" />
            WF-01 — Z-Document Upload
            {docFilename && (
              <span className="lvwf-pipeline-file">{docFilename}</span>
            )}
          </div>
          {STEPS.map((s) => {
            const st = getStepState(s.id);
            return (
              <div key={s.id} className={`lvwf-step ${st}`}>
                <div className="lvwf-step-num">
                  {st === "completed" ? "✓" : s.id}
                </div>
                <div className="lvwf-step-text">{s.label}</div>
                {st === "active" && <span className="lvwf-step-spin" />}
              </div>
            );
          })}

          {/* Completed state — minimal, Option A */}
          {phase === "completed" && (
            <div className="lvwf-completed-footer">
              <button className="ds-btn ds-btn--secondary" onClick={reset}>
                ⬆ Upload Another
              </button>
            </div>
          )}
        </div>
      )}

      {/* Production Compliance Specification — template switcher + active view */}
      <section className="ds-card cp-spec" aria-labelledby="zdwf-spec-title">
        <div className="cp-spec-head">
          <h3 id="zdwf-spec-title" className="ds-section-title">Production compliance specification</h3>
          <div className="cp-mode-switch" role="group" aria-label="Specification template">
            {COMPLIANCE_MODES.map((m) => (
              <button
                key={m.id}
                type="button"
                className="ds-chip"
                aria-pressed={complianceMode === m.id}
                onClick={() => setComplianceMode(m.id)}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>
        {complianceMode === "discovery"
          ? <DiscoveryView key={`d${docsVersion}`} database={database} onRunApproved={onRunApproved} initialDocId={initialDiscoverDoc} />
          : complianceMode === "pipeline"
          ? <CompliancePipeline key={`p${docsVersion}`} database={database} />
          : complianceMode === "products"
          ? <ProductSheets key={`ps${docsVersion}`} database={database} />
          : <ComplianceSheet key={`q${docsVersion}`} database={database} />}
      </section>
    </div>
  );
}
